// Pending — what a screen shows while a read is being made.
//
// 3 Sept 2026 audit: fourteen screens said a bare "Loading…" with no way to
// tell a slow read from a dead one (the Mac's books stop when Docker is not
// running, and the screen sat on "Loading…" for good). One component: a quiet
// line first, and after eight seconds a plain "still reading" line.
//
// 22 Oct 2026 (design system, docs/DESIGN-SYSTEM.md §3 — a state is ONE line,
// ≤ 10 words, and explanation lives behind a tap): the slow state used to be a
// 27-word sentence that named the desk Mac and Docker on EVERY screen — wrong
// on the KYT screen (it reads the screening worker) and on the energy desk. It
// is now one neutral line that is true anywhere; the books' sentence, kept
// whole, is what "Details" opens. Only phrasing content here (span, button), so
// `inline` still sits inside a <p> or a table cell.
//
// This does not import kit.tsx's StateLine: kit re-exports Pending, and
// Ledger → DealSlideOver → Pending → kit → Ledger would be a cycle. It draws the
// same line — body size, muted, "Details" at the right, the explanation under a
// rule — from the same roles.
import { useEffect, useState } from 'react';

const SLOW_MS = 8_000;

/** The slow state's one line: what is happening and what to do. Neutral — true on every screen. */
export const PENDING_SLOW_LINE = 'Still reading — longer than usual. Refresh if it stays.';
/** The old sentence, behind "Details". It is about the books, and says so: another screen's cause is its own. */
// words:ok — behind "Details": explanation lives behind a tap (docs/DESIGN-SYSTEM.md §3), it is never a line he reads by default
export const PENDING_SLOW_WHY = 'On a books screen, the books live on the desk Mac — if Docker is not running there, nothing will load. Otherwise pull to refresh.';

/** The slow state as drawn: the one line, "Details", and — once tapped — the explanation. Separate from `Pending` so a test or a
 *  picture can draw it without waiting eight seconds; `Pending` owns the timer and the tap. */
export function PendingSlow({ open = false, onToggle }: { open?: boolean; onToggle?: () => void }) {
  return (
    <>
      {/* wraps like StateLine's row: "Details" sits beside the line, or under it flush left — never indented */}
      <span className="inline-flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span>{PENDING_SLOW_LINE}</span>
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="tap-44 text-meta underline-offset-2 hover:text-tg-text hover:underline"
        >
          {open ? 'Less' : 'Details'}
        </button>
      </span>
      {open && <span className="mt-1.5 block border-l border-stroke pl-3 text-meta">{PENDING_SLOW_WHY}</span>}
    </>
  );
}

export function Pending({ what = 'Reading the books…', inline = false }: { what?: string; inline?: boolean }) {
  const [slow, setSlow] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(t);
  }, []);
  const Tag = inline ? 'span' : 'p';
  return (
    <Tag className="text-tg-hint text-body">
      {slow ? <PendingSlow open={open} onToggle={() => setOpen((v) => !v)} /> : what}
    </Tag>
  );
}
